import React, { FC, useEffect } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';

import { isSafari } from 'utils/Browser';
import {
  CheckoutPayResponse,
  SourceType,
  WalletResponse,
  Customer,
} from 'services/checkout-service/models';
import { MerchantPaymentConfig } from 'services/cos-service/models';
import { useMerchantPaymentConfigContext } from 'hooks/context/merchantPaymentConfig';
import { GpayButton } from './GpayButton';
import { ApplePayButton } from './ApplePayButton';
import { SetPaymentOverlayType } from '../CloverPaymentSection';
import { WALLET_BUTTONS_TEST_IDS } from './const';

import styles from './styles.module.scss';

export type NotifyWalletOfPaymentType = (checkoutPayResponse?: CheckoutPayResponse) => void;
export type WalletSubmitPaymentType = (
  walletResponse: WalletResponse,
  notifyCallback: NotifyWalletOfPaymentType,
  sourceType: SourceType,
  recaptchaToken: string,
) => Promise<void>;

export type WalletButtonProps = {
  cloverInstance?: Clover;
  cloverElementsInstance?: Clover.ElementRoot;
  total: number;
  isInvoiceMode: boolean;
  checkoutSessionId?: string;
  successUrl?: string;
  onWalletSubmit: WalletSubmitPaymentType;
  setPaymentOverlays: SetPaymentOverlayType;
  customerInfo?: Partial<Customer>;
  isRecaptchaEnabled: boolean;
  recaptchaRef: React.RefObject<ReCAPTCHA>;
};

const determineWalletType = (
  cloverInstance: Clover,
  paymentReqData: any,
  merchantPaymentConfig: Maybe<MerchantPaymentConfig>,
) => {
  if (!merchantPaymentConfig) return undefined;

  let paymentRequest: Maybe<PaymentRequest> = undefined;
  try {
    // will throw if PaymentRequest API unsupported in browser (FireFox desktop/mobile & Android WebView)
    paymentRequest = cloverInstance.paymentRequest(paymentReqData);
  } catch (e) {
    console.error(e);
  }
  const gpayBrowserSupport = paymentRequest?.canMakePayment().then((supportsGpay: boolean) => supportsGpay);
  const backendSupportsGpay = merchantPaymentConfig.gpay?.supported;
  const backendSupportsApplePay = merchantPaymentConfig.apple_pay?.supported;

  const isApplePaySupported = isSafari && backendSupportsApplePay;
  const isGPaySupported = gpayBrowserSupport && backendSupportsGpay;

  if (isApplePaySupported && isGPaySupported) {
    return 'BOTH';
  }

  if (isApplePaySupported) {
    return 'APPLE_PAY';
  }

  if (isGPaySupported) {
    return 'G_PAY';
  }

  return undefined;
};

const WalletButtons: FC<WalletButtonProps> = ({
  cloverInstance,
  cloverElementsInstance,
  total,
  onWalletSubmit,
  setPaymentOverlays,
  customerInfo,
  isRecaptchaEnabled,
  recaptchaRef,
}) => {
  const { merchantPaymentConfig } = useMerchantPaymentConfigContext();
  const paymentReqData = {
    total: {
      label: 'Clover checkout',
      amount: total,
    },
  };
  const type = cloverInstance && determineWalletType(cloverInstance, paymentReqData, merchantPaymentConfig);
  // const [recaptchaLocalRef, setRecapthaLocalRef] = useState(recaptchaRef);
  // //
  useEffect(() => {
    if(recaptchaRef?.current) {
      // @ts-ignore
      window["recaptchaRef"] = recaptchaRef
    }
  }, [recaptchaRef]);

  return (
    <section data-testid={WALLET_BUTTONS_TEST_IDS.walletButtons} className={styles.walletButtons}>
      {(type === 'G_PAY' || type === 'BOTH') && (
        <GpayButton
          paymentReqData={paymentReqData}
          cloverElementsInstance={cloverElementsInstance}
          onWalletSubmit={onWalletSubmit}
          setPaymentOverlays={setPaymentOverlays}
          customerInfo={customerInfo}
          isRecaptchaEnabled={isRecaptchaEnabled}
        />
      )}

      {(type === 'APPLE_PAY' || type === 'BOTH') && (
        <ApplePayButton
          subtotal={total}
          onWalletSubmit={onWalletSubmit}
          setPaymentOverlays={setPaymentOverlays}
          isRecaptchaEnabled={isRecaptchaEnabled}
        />
      )}
    </section>
  );
};

export { WalletButtons };
